import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import cn from 'classnames'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { isHydratedAnswerCacheEntry, isViewerHydratedAnswerCacheEntry } from 'state/cache'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import {
    MIN_ANSWER_GEOMETRY_HEIGHT,
    rememberRetainedAnswerGeometry,
    resolveRetainedAnswerGeometry,
    resolveInitialAnswerGeometry,
    resolveVirtualAnswerContainerStyle,
} from 'lib/answerGeometryFrame'
import { answerHeightContentSignature } from 'lib/answerHeightFingerprint'
import styles from 'components/answers/Answer/answer.module.scss'
import useAnswerChannelRetention from './useAnswerChannelRetention'
import { resolveAnswerInViewAnswer } from './answerInViewAnswer'
import { COLLAPSE_POST_LENGTH, shouldCollapseAnswerText } from './answerCollapseState'

const getElementBoxSize = element => {
    if (!element) return { height: 0, width: 0 }

    const rect = element.getBoundingClientRect?.()
    return {
        height: Math.max(element.offsetHeight || 0, rect?.height || 0),
        width: Math.max(element.clientWidth || 0, rect?.width || 0),
    }
}

const resolveVirtualRowElement = element => {
    const row = element?.closest?.('[data-virtual-answer-row="true"]')
    return row instanceof HTMLElement ? row : null
}

export default function VirtualAnswerInView({
    answerId,
    children,
    estimatedHeight = 0,
    estimatedWidth = 0,
    isActive = false,
    isTitle = false,
    isThin = false,
    postLens = false,
    staticGeometry = false,
    onTextCollapsedChange = null,
    textIsCollapsedOverride = null,
}) {
    const answer = resolveAnswerInViewAnswer({
        cachedAnswer: useCachedAnswer(answerId),
        propAnswer: children?.props?.answer,
    })
    const isHydratedAnswer = isHydratedAnswerCacheEntry(answer)
    const needsAnswerHydration = !isViewerHydratedAnswerCacheEntry(answer)
    const initialTextIsCollapsed = () =>
        textIsCollapsedOverride == null ? shouldCollapseAnswerText(answer) : textIsCollapsedOverride
    const initialTextIsCollapsedValue = initialTextIsCollapsed()
    const [textIsCollapsed, setTextIsCollapsed] = useState(initialTextIsCollapsedValue)
    const containerRef = useRef(null)
    const userToggledCollapseRef = useRef(false)
    const initialGeometry = resolveInitialAnswerGeometry({
        answerId,
        answer,
        estimatedHeight,
        estimatedWidth,
        isActive,
        isThin,
        isTitle,
        postLens,
        textIsCollapsed: initialTextIsCollapsedValue,
        useContentEstimate: !staticGeometry,
        useRememberedHeight: !staticGeometry,
    })
    const [retainedHeight, setRetainedHeight] = useState(initialGeometry.height)
    const retainedHeightRef = useRef(initialGeometry.height)
    const lastMeasuredHeightRef = useRef(initialGeometry.height)
    const hasMeasuredHeightRef = useRef(initialGeometry.hasRememberedHeight)
    const allowNextMeasuredShrinkRef = useRef(false)
    const textIsCollapsedRef = useRef(initialTextIsCollapsedValue)
    const shouldRender = isHydratedAnswer

    const setTextIsCollapsedWithUserIntent = useCallback(
        value => {
            userToggledCollapseRef.current = true
            allowNextMeasuredShrinkRef.current = true
            const currentValue = textIsCollapsedRef.current
            const nextValue = typeof value === 'function' ? value(currentValue) : value
            // Oversized read-only previews can expose a native "more" control even when the
            // coarse collapse boolean is already false. Still report the explicit expand
            // intent so static parent rows can resize for the full-content render.
            if (nextValue !== currentValue || nextValue === false)
                onTextCollapsedChange?.(nextValue)
            textIsCollapsedRef.current = nextValue
            setTextIsCollapsed(nextValue)
        },
        [onTextCollapsedChange],
    )

    useEffect(() => {
        textIsCollapsedRef.current = textIsCollapsed
    }, [textIsCollapsed])

    useAnswerChannelRetention(answerId, { isVisible: true, answer })

    useEffect(() => {
        if (!needsAnswerHydration || !answerId) return
        queueAnswerHydration(answerId, { lane: 'visible' })
    }, [answerId, needsAnswerHydration])

    useEffect(() => {
        if (textIsCollapsedOverride != null) {
            setTextIsCollapsed(textIsCollapsedOverride)
            return
        }
        if (userToggledCollapseRef.current) return
        setTextIsCollapsed(shouldCollapseAnswerText(answer))
    }, [answerId, answer, textIsCollapsedOverride])

    const retainVirtualRowHeight = useCallback(
        ({ height, width }, { allowShrink = false, preserveHeight = true } = {}) => {
            if (staticGeometry) return

            const userAllowedShrink = allowShrink && allowNextMeasuredShrinkRef.current
            const nextHeight = resolveRetainedAnswerGeometry({
                measuredHeight: height,
                previousHeight: retainedHeightRef.current,
                previousMeasuredHeight: lastMeasuredHeightRef.current,
                hasMeasuredHeight: hasMeasuredHeightRef.current,
                preserveHeight: preserveHeight && !userAllowedShrink,
                allowShrink: userAllowedShrink,
                isThin,
                minHeight: MIN_ANSWER_GEOMETRY_HEIGHT,
            })
            if (!nextHeight) return

            lastMeasuredHeightRef.current = nextHeight
            hasMeasuredHeightRef.current = true
            if (userAllowedShrink) {
                allowNextMeasuredShrinkRef.current = false
            }

            rememberRetainedAnswerGeometry({
                answerId,
                height: nextHeight,
                width,
                isActive,
                isThin,
                isTitle,
                postLens,
                textIsCollapsed,
                contentSignature: answerHeightContentSignature(answer),
            })

            const previousRetainedHeight = retainedHeightRef.current
            retainedHeightRef.current = nextHeight

            if (shouldRender || Math.abs(previousRetainedHeight - nextHeight) <= 1) return
            setRetainedHeight(nextHeight)
        },
        [
            answer,
            answerId,
            isActive,
            isThin,
            isTitle,
            postLens,
            shouldRender,
            staticGeometry,
            textIsCollapsed,
        ],
    )

    const measureVirtualRowHeight = useCallback(
        options => {
            if (staticGeometry) return

            const row = resolveVirtualRowElement(containerRef.current)
            const measurement = getElementBoxSize(row)
            retainVirtualRowHeight(measurement, options)
        },
        [retainVirtualRowHeight, staticGeometry],
    )

    const refreshEstimatedGeometry = useCallback(() => {
        if (staticGeometry) return
        if (hasMeasuredHeightRef.current && isHydratedAnswer) return

        const nextGeometry = resolveInitialAnswerGeometry({
            answerId,
            answer,
            estimatedHeight,
            estimatedWidth,
            isActive,
            isThin,
            isTitle,
            postLens,
            textIsCollapsed,
            useRememberedHeight: true,
        })
        const nextHeight = resolveRetainedAnswerGeometry({
            measuredHeight: nextGeometry.height,
            previousHeight: retainedHeightRef.current,
            previousMeasuredHeight: lastMeasuredHeightRef.current,
            hasMeasuredHeight: hasMeasuredHeightRef.current,
            preserveHeight: hasMeasuredHeightRef.current,
            isThin,
            minHeight: MIN_ANSWER_GEOMETRY_HEIGHT,
        })
        if (!nextHeight || Math.abs(retainedHeightRef.current - nextHeight) <= 1) return

        retainedHeightRef.current = nextHeight
        if (!shouldRender) setRetainedHeight(nextHeight)
    }, [
        answer,
        answerId,
        estimatedHeight,
        estimatedWidth,
        isActive,
        isHydratedAnswer,
        isThin,
        isTitle,
        postLens,
        shouldRender,
        staticGeometry,
        textIsCollapsed,
    ])

    const rememberVirtualRowHeight = useCallback(
        options => {
            measureVirtualRowHeight(options)
        },
        [measureVirtualRowHeight],
    )

    const scheduleRememberVirtualRowHeight = useCallback(
        options => {
            if (staticGeometry) return null
            if (typeof window === 'undefined') return null

            if (typeof window.requestAnimationFrame === 'function') {
                const frameId = window.requestAnimationFrame(() =>
                    rememberVirtualRowHeight(options),
                )
                return () => window.cancelAnimationFrame?.(frameId)
            }

            const timeoutId = window.setTimeout(() => rememberVirtualRowHeight(options), 0)
            return () => window.clearTimeout(timeoutId)
        },
        [rememberVirtualRowHeight, staticGeometry],
    )

    useEffect(() => {
        refreshEstimatedGeometry()
    }, [refreshEstimatedGeometry])

    useLayoutEffect(() => {
        if (staticGeometry) return
        if (!shouldRender) return

        const row = resolveVirtualRowElement(containerRef.current)
        if (!row) return

        let cancelScheduledMeasure = scheduleRememberVirtualRowHeight()
        if (typeof ResizeObserver === 'undefined') {
            return () => {
                cancelScheduledMeasure?.()
            }
        }

        const observer = new ResizeObserver(() => {
            cancelScheduledMeasure?.()
            cancelScheduledMeasure = scheduleRememberVirtualRowHeight({
                allowShrink: allowNextMeasuredShrinkRef.current,
            })
        })
        observer.observe(row)

        return () => {
            observer.disconnect()
            cancelScheduledMeasure?.()
        }
    }, [scheduleRememberVirtualRowHeight, shouldRender, staticGeometry])

    const intrinsicHeight = Math.max(
        MIN_ANSWER_GEOMETRY_HEIGHT,
        retainedHeight || MIN_ANSWER_GEOMETRY_HEIGHT,
    )
    const inViewCallback = useCallback(
        options => {
            if (staticGeometry) return

            // Same latch the text collapse toggle arms: a deliberate viewer action (e.g. a
            // payload flipped from grid to carousel) may shrink the retained row height once.
            if (options?.userIntent) allowNextMeasuredShrinkRef.current = true
            scheduleRememberVirtualRowHeight({
                allowShrink: Boolean(options?.allowShrink),
            })
        },
        [scheduleRememberVirtualRowHeight, staticGeometry],
    )
    // React Virtual owns rendered row geometry. Keep a fixed frame only for
    // pre-hydration placeholders so measured rows do not inherit stale floors.
    const containerStyle = resolveVirtualAnswerContainerStyle({
        intrinsicHeight,
        shouldRender,
    })

    return (
        <div
            ref={containerRef}
            className={cn(
                styles.answerInView,
                !shouldRender && styles.answerInViewPlaceholder,
                !shouldRender && isTitle && styles.answerInViewTitlePlaceholder,
                `answer-in-view-${answerId}`,
            )}
            style={containerStyle}
        >
            {shouldRender &&
                React.cloneElement(children, {
                    key: `answer-${answerId}`,
                    answer,
                    inViewCallback,
                    textIsCollapsed,
                    setTextIsCollapsed: setTextIsCollapsedWithUserIntent,
                    COLLAPSE_POST_LENGTH,
                    estimatedWidth,
                    staticGeometry,
                })}
        </div>
    )
}
